import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatCurrency } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  ClipboardList,
  AlertTriangle,
  Clock,
  DollarSign,
  Timer,
  Box,
  ArrowRight,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  
  const absMinutes = Math.abs(Math.round(minutes));
  
  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${absMinutes}m`;
}

export default function RoomIntelligenceSection({ roomId }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/intelligence/rooms/${roomId}?days=90`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch room intelligence:', error);
    } finally {
      setLoading(false);
    }
  }, [roomId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!data) {
    return null;
  }
  
  const { kpis, top_problematic_assets } = data;
  
  return (
    <Card data-testid="room-intelligence-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-brand" />
          {t('intelligence.roomIntelligence')}
        </CardTitle>
        <CardDescription>{t('intelligence.last90Days')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPI Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <ClipboardList className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{kpis.total_wos}</p>
            <p className="text-xs text-muted-foreground">{t('intelligence.totalWOs')}</p>
          </div>
          
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Clock className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{kpis.open_wos}</p>
            <p className="text-xs text-muted-foreground">{t('intelligence.openWOs')}</p>
          </div>
          
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <DollarSign className="w-4 h-4 text-green-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{formatCurrency(kpis.total_cost)}</p>
            <p className="text-xs text-muted-foreground">{t('intelligence.totalCost')}</p>
          </div>
          
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Timer className="w-4 h-4 text-brand mx-auto mb-1" />
            <p className="text-xl font-bold">{kpis.avg_mttr_minutes ? formatDuration(kpis.avg_mttr_minutes) : '—'}</p>
            <p className="text-xs text-muted-foreground">{t('intelligence.avgMTTR')}</p>
          </div>
        </div>
        
        {/* Top Problematic Assets */}
        {top_problematic_assets.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {t('intelligence.topProblematicAssets')}
            </h4>
            <div className="space-y-2">
              {top_problematic_assets.map(asset => (
                <div 
                  key={asset.asset_id}
                  className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/assets/${asset.asset_id}`)}
                  data-testid={`problematic-asset-${asset.asset_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Box className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{asset.asset_name}</p>
                      <p className="text-xs text-muted-foreground">{asset.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={asset.open_wos > 0 ? 'destructive' : 'secondary'}>
                      {asset.total_wos} WOs
                    </Badge>
                    <ArrowRight className={`w-4 h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
